import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { useAction, useItem, useList } from '../api/queries'
import type { ImportBatchDetail, ImportBatchList, ImportReport, ReportItem } from '../api/types'
import { useUser } from '../auth/AuthContext'
import { DataTable, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { Card, ErrorState, Field, LoadingState, PageHeader, PillFilter, StatusBadge } from '../components/ui'
import { formatDateTime, humanize } from '../lib/format'
import { can } from '../lib/permissions'

type Flat = ReportItem & { section: string }

export function flattenReport(report: ImportReport): Flat[] {
  return Object.entries(report.sections ?? {}).flatMap(([section, items]) => items.map((i) => ({ ...i, section })))
}

function Report({ batch }: { batch: ImportBatchDetail }) {
  const user = useUser()
  const client = useQueryClient()
  const report = batch.report as unknown as ImportReport
  const items = useMemo(() => flattenReport(report), [report])
  const [severity, setSeverity] = useState('')
  const [kind, setKind] = useState('')
  const [withPhotos, setWithPhotos] = useState(false)
  const commit = useAction<{ with_photos: boolean }, ImportBatchDetail>('post', `/api/imports/${batch.id}/commit/`)
  const kinds = [...new Set(items.map((i) => i.kind).filter(Boolean))] as string[]
  const shown = items.filter((i) => (!severity || i.severity === severity) && (!kind || i.kind === kind))
  const count = (s: string) => items.filter((i) => i.severity === s).length
  const columns: Column<Flat>[] = [
    { key: 's', header: 'Severidad', cell: (i) => <StatusBadge tone={i.severity === 'error' ? 'danger' : i.severity === 'warning' ? 'warn' : 'info'} label={humanize(i.severity)} /> },
    { key: 'k', header: 'Tipo', cell: (i) => humanize(i.kind) },
    { key: 'm', header: 'Mensaje', cell: (i) => i.message },
    { key: 'w', header: 'Ubicación', cell: (i) => <span className="mono small">{[i.equipment, i.sheet, i.ref].filter(Boolean).join(' · ') || i.file}</span>, priority: 'low' },
  ]
  async function doCommit() {
    await commit.mutateAsync({ with_photos: withPhotos })
    await client.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/api/imports') })
  }
  return (
    <Card title={`Lote ${batch.id}: ${humanize(batch.status)}`} actions={<a className="btn btn--ghost btn--sm" href={`/api/imports/${batch.id}/report/`}><Icon name="download" size={14} /> Descargar informe CSV</a>}>
      {batch.status === 'validated' ? <p role="note" className="callout">Validación completa: aún no se ha escrito nada. Revisar el informe y luego confirmar la importación.</p> : null}
      {batch.status === 'committed' ? <p role="note" className="callout">Importación confirmada. {Object.entries(report.commit?.counters ?? {}).map(([k, v]) => `${humanize(k)}: ${v}`).join(' · ')}</p> : null}
      {batch.error ? <ErrorState error={new Error(batch.error)} /> : null}
      <dl className="stats">{Object.entries(report.summary ?? {}).filter(([, v]) => typeof v === 'number').slice(0, 12).map(([k, v]) => <div key={k}><dt>{humanize(k)}</dt><dd className="mono">{String(v)}</dd></div>)}</dl>
      <div className="toolbar">
        <div className="pills" role="group" aria-label="Severidad">
          <PillFilter active={severity === ''} onClick={() => setSeverity('')} count={items.length}>Todas las severidades</PillFilter>
          {(['error', 'warning', 'info'] as const).map((s) => <PillFilter key={s} active={severity === s} onClick={() => setSeverity(s)} count={count(s)}>{humanize(s)}</PillFilter>)}
        </div>
        <Field label="Tipo" htmlFor="imp-kind"><select id="imp-kind" value={kind} onChange={(e) => setKind(e.target.value)}><option value="">Todos los tipos</option>{kinds.map((k) => <option key={k} value={k}>{humanize(k)}</option>)}</select></Field>
      </div>
      <DataTable caption="Informe de validación" columns={columns} rows={shown} rowKey={(i) => `${i.section}-${i.file}-${i.ref}-${i.message}`} emptyTitle="Nada coincide con estos filtros" />
      {report.ignored_files?.length ? <p className="faint small">{report.ignored_files.length} archivos del sistema ignorados (por ejemplo, archivos ._ y ~$).</p> : null}
      {can(user.role, 'import.commit') && batch.status === 'validated' ? (
        <div className="form-stack">
          <label className="check"><input type="checkbox" checked={withPhotos} onChange={(e) => setWithPhotos(e.target.checked)} /> Incluir las fotos incrustadas en las inspecciones y en la galería de cada equipo</label>
          <div className="row"><button type="button" className="btn btn--primary" disabled={commit.isPending} onClick={() => void doCommit()}>Confirmar importación</button><span className="muted">Crea los registros en una transacción por archivo; repetir la importación no duplica datos.</span></div>
        </div>
      ) : null}
      {commit.isError ? <p role="alert" className="form-error">{commit.error.message}</p> : null}
    </Card>
  )
}

export function ImportsPage() {
  const client = useQueryClient()
  const [files, setFiles] = useState<File[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const history = useList<ImportBatchList>('/api/imports/', { ordering: '-id' })
  const active = useItem<ImportBatchDetail>(activeId ? `/api/imports/${activeId}/` : null)
  async function upload(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null)
    try {
      const form = new FormData()
      files.forEach((f) => form.append('files', f))
      const batch = await api.upload<ImportBatchDetail>('/api/imports/', form)
      setActiveId(batch.id)
      await client.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/api/imports') })
    } catch (err) { setError(err instanceof Error ? err.message : 'La carga falló.') } finally { setBusy(false) }
  }
  const columns: Column<ImportBatchList>[] = [
    { key: 'id', header: 'Lote', cell: (b) => `#${b.id}` },
    { key: 'st', header: 'Estado', cell: (b) => <StatusBadge tone={b.status === 'committed' ? 'ok' : b.status === 'failed' ? 'danger' : 'info'} label={humanize(b.status)} /> },
    { key: 'f', header: 'Archivos', cell: (b) => b.file_count },
    { key: 'by', header: 'Creado por', cell: (b) => b.created_by_name || '—', priority: 'low' },
    { key: 'at', header: 'Creado', cell: (b) => formatDateTime(b.created_at), priority: 'low' },
  ]
  return (
    <div className="page">
      <PageHeader title="Importaciones" subtitle="Cargar, validar, revisar y luego confirmar. La validación nunca escribe datos" />
      <Card title="Cargar hojas de cálculo">
        <form className="form-stack" onSubmit={upload}>
          <Field label="Archivos de hojas de cálculo" htmlFor="imp-files" hint="La tabla consolidada de diagnóstico, la tabla de cargas, la lista de equipos y los libros de cada equipo (.xls, .xlsx)."><input id="imp-files" type="file" multiple accept=".xls,.xlsx" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} /></Field>
          {files.length ? <p className="muted">{files.length} {files.length === 1 ? 'archivo seleccionado' : 'archivos seleccionados'}.</p> : null}
          {error ? <p role="alert" className="form-error">{error}</p> : null}
          <button className="btn btn--primary" disabled={busy || files.length === 0}>{busy ? 'Validando…' : 'Validar archivos'}</button>
        </form>
      </Card>
      {activeId ? (active.isPending ? <LoadingState /> : active.isError ? <ErrorState error={active.error} /> : <Report batch={active.data} />) : null}
      <Card title="Historial de lotes"><DataTable caption="Lotes de importación" columns={columns} rows={history.data?.results} loading={history.isPending} error={history.error} rowKey={(b) => b.id} onRowClick={(b) => setActiveId(b.id)} rowLabel={(b) => `Abrir lote ${b.id}`} emptyTitle="Aún no hay importaciones" /></Card>
    </div>
  )
}
